import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals';
import { useCreateEncounterFromAppointmentMutation } from '@features/appointments';
import {
  clearActiveWorkflowEncounter,
  selectActiveWorkflowEncounterId,
  selectIsWorkflowStale,
  selectLastAccessedAt,
} from '@features/encounters';
import { bindWaitingRoomLaunchToEncounter } from '@features/telehealth/store';
import type { TelehealthEncounterRouteState } from '@features/telehealth/types';
import { SCHEDULED_APPOINTMENT_REQUIRED_MESSAGE, useNoteCreationPolicy } from '@features/user';
import { useAppDispatch, useAppSelector, useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useRecordingContext } from '../context/RecordingContext';

import { useAppointmentCollision } from './useAppointmentCollision';

/**
 * Type guard to safely check if an object has an encounter_id property
 */
function hasEncounterId(encounter: unknown): encounter is { encounter_id: string } {
  return (
    encounter !== null &&
    typeof encounter === 'object' &&
    'encounter_id' in encounter &&
    typeof (encounter as Record<string, unknown>).encounter_id === 'string'
  );
}

interface StartRecordingOptions {
  forceAppointmentSelection?: boolean;
  routeState?: TelehealthEncounterRouteState;
  bindWaitingRoomLaunch?: boolean;
}

/**
 * Hook for managing recording session navigation and context.
 * Routes every recording launch into the encounter flow and detects appointment
 * conflicts to suggest the best recording context.
 */
export const useRecordingSession = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { open: openModal } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { requiresScheduledAppointmentForNoteCreation } = useNoteCreationPolicy();
  const {
    appointmentData,
    isLoading,
    recommendation,
    hasCollision,
    eligibleCurrentAppointments,
    eligibleOvertimeAppointments,
    eligibleNextAppointments,
  } = useAppointmentCollision();
  const [createEncounterFromAppointment] = useCreateEncounterFromAppointmentMutation();

  // Get recording status and active encounter for bypass logic
  const { recordingStatus } = useRecordingContext();
  const activeWorkflowEncounterId = useAppSelector(selectActiveWorkflowEncounterId) as
    | string
    | null
    | undefined;
  const isWorkflowStale = useAppSelector(selectIsWorkflowStale);
  const lastAccessedAt = useAppSelector(selectLastAccessedAt) as number | null | undefined;

  const recordingPath = '/encounters/active';

  const eligibleAppointmentCount = useMemo(
    () =>
      eligibleCurrentAppointments.length +
      eligibleOvertimeAppointments.length +
      eligibleNextAppointments.length,
    [
      eligibleCurrentAppointments.length,
      eligibleOvertimeAppointments.length,
      eligibleNextAppointments.length,
    ],
  );

  const canStartRecording = useMemo(
    () =>
      !requiresScheduledAppointmentForNoteCreation ||
      // Keep the button/action state permissive once a recording is already active; the guard
      // only needs to block new unscheduled recording launches.
      recordingStatus.isRecording ||
      eligibleAppointmentCount > 0,
    [
      eligibleAppointmentCount,
      recordingStatus.isRecording,
      requiresScheduledAppointmentForNoteCreation,
    ],
  );

  const startRecording = useCallback(
    async (options?: StartRecordingOptions) => {
      const navigateToEncounter = (encounterId: string, replace = false) => {
        if (options?.bindWaitingRoomLaunch) {
          dispatch(bindWaitingRoomLaunchToEncounter(encounterId));
        }
        navigate(`/encounters/${encounterId}`, { replace, state: options?.routeState });
      };

      const navigateToActiveEncounter = () => {
        navigate('/encounters/active', { state: options?.routeState });
      };

      const blockUnscheduledRecording = () => {
        enqueueSnackbar(SCHEDULED_APPOINTMENT_REQUIRED_MESSAGE, {
          variant: 'warning',
        });
        navigate('/schedule');
      };

      // If actively recording, bypass collision detection and navigate to active encounter
      // This prevents showing collision modal when user is already recording
      if (recordingStatus.isRecording) {
        if (activeWorkflowEncounterId) {
          // GDN-182: Validate the persisted encounter ID before trusting it.
          // If stale (>1h inactive) or rehydrated from storage without a timestamp,
          // clear it and fall through to collision detection instead of routing
          // to a potentially week-old encounter.
          if (isWorkflowStale || lastAccessedAt === null) {
            dispatch(clearActiveWorkflowEncounter({ reason: 'stale' }));
          } else {
            // User is recording on a valid, recent encounter - take them there
            navigateToEncounter(activeWorkflowEncounterId);
            return;
          }
        }
        // No valid encounter ID — fall through to collision detection
      }

      // Check for appointment collision
      if (isLoading) {
        if (requiresScheduledAppointmentForNoteCreation) {
          return;
        }
        navigateToActiveEncounter();
        return;
      }

      if (options?.forceAppointmentSelection && eligibleAppointmentCount > 0) {
        openModal(MODAL_TYPES.APPOINTMENT_SELECTION, {
          eligibleCurrent: eligibleCurrentAppointments,
          eligibleOvertime: eligibleOvertimeAppointments,
          eligibleNext: eligibleNextAppointments,
          recommendation,
          telehealthLaunch: true,
        });
        return;
      }

      // If high confidence match, auto-navigate to the appointment's encounter
      if (recommendation?.confidence === 'high') {
        const appointment = recommendation.appointment;
        try {
          // Use type guard to safely check for encounter_id
          if (hasEncounterId(appointment.encounter)) {
            // Encounter already exists, navigate to it
            navigateToEncounter(appointment.encounter.encounter_id);
          } else {
            // Create encounter and navigate
            const encounter = await createEncounterFromAppointment({
              appointmentId: appointment.appointment_id,
            }).unwrap();
            if (encounter?.encounter_id) {
              navigateToEncounter(encounter.encounter_id);
            } else {
              throw new Error('Failed to get encounter ID');
            }
          }
        } catch (error) {
          console.error('Failed to create encounter from appointment:', error);
          if (requiresScheduledAppointmentForNoteCreation) {
            enqueueSnackbar('Could not open the scheduled appointment for recording.', {
              variant: 'error',
            });
            navigate('/schedule');
            return;
          }
          navigateToActiveEncounter();
        }
        return;
      }

      // If medium confidence or multiple options, show selection modal
      if (hasCollision) {
        openModal(MODAL_TYPES.APPOINTMENT_SELECTION, {
          eligibleCurrent: eligibleCurrentAppointments,
          eligibleOvertime: eligibleOvertimeAppointments,
          eligibleNext: eligibleNextAppointments,
          recommendation,
          telehealthLaunch: Boolean(options?.routeState?.telehealthLaunch),
        });
        return;
      }

      if (requiresScheduledAppointmentForNoteCreation) {
        blockUnscheduledRecording();
        return;
      }

      // No collisions, go to active encounter
      navigateToActiveEncounter();
    },
    [
      navigate,
      dispatch,
      recordingStatus.isRecording,
      activeWorkflowEncounterId,
      isWorkflowStale,
      lastAccessedAt,
      isLoading,
      requiresScheduledAppointmentForNoteCreation,
      eligibleAppointmentCount,
      recommendation,
      hasCollision,
      openModal,
      enqueueSnackbar,
      createEncounterFromAppointment,
      eligibleCurrentAppointments,
      eligibleOvertimeAppointments,
      eligibleNextAppointments,
    ],
  );

  return {
    startRecording,
    recordingPath,
    appointmentCollision: appointmentData,
    hasAppointmentCollision: hasCollision,
    isLoadingAppointments: isLoading,
    canStartRecording,
    requiresScheduledAppointmentForNoteCreation,
  };
};
